import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { APIError, type UpdateStageTemplateInput } from "@/adapters/api";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCreateStageTemplate } from "../../hooks/use-create-stage-template.ts";
import { useUpdateStageTemplate } from "../../hooks/use-update-stage-template.ts";
import {
	type TemplateFormValues,
	templateSchema,
} from "../../schemas/template-schema.ts";
import type { StageTemplate } from "../../types.ts";

const VIOLATION_FIELDS: Record<string, keyof TemplateFormValues> = {
	name: "name",
	description: "description",
};

export function TemplateFormDialog({
	open,
	onOpenChange,
	mode,
	template,
	onCreated,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	mode: "create" | "edit";
	template?: StageTemplate;
	onCreated?: (templateId: string) => void;
}) {
	const [formError, setFormError] = useState<string | null>(null);
	const createTemplate = useCreateStageTemplate();
	const updateTemplate = useUpdateStageTemplate(template?.id);
	const isPending = createTemplate.isPending || updateTemplate.isPending;

	const {
		register,
		handleSubmit,
		reset,
		setError,
		formState: { errors },
	} = useForm<TemplateFormValues>({
		resolver: zodResolver(templateSchema),
		defaultValues: { name: "", description: "" },
	});

	useEffect(() => {
		if (open) {
			reset({
				name: template?.name ?? "",
				description: template?.description ?? "",
			});
			setFormError(null);
		}
	}, [open, template, reset]);

	function applyAPIError(err: unknown, fallback: string) {
		if (err instanceof APIError) {
			let matchedField = false;
			for (const [field, message] of Object.entries(err.violations)) {
				const formField = VIOLATION_FIELDS[field];
				if (formField) {
					setError(formField, { message });
					matchedField = true;
				}
			}
			if (!matchedField) setFormError(err.message || fallback);
			return;
		}
		setFormError(err instanceof Error ? err.message : fallback);
	}

	async function onSubmit(values: TemplateFormValues) {
		setFormError(null);
		const description = values.description?.trim() || undefined;

		if (mode === "create") {
			try {
				const created = await createTemplate.mutateAsync({
					name: values.name,
					description,
				});
				toast.success("Template criado com sucesso!");
				onOpenChange(false);
				onCreated?.(created.id);
			} catch (err) {
				applyAPIError(err, "Houve um erro ao criar o template.");
			}
			return;
		}

		if (!template) return;
		const input: UpdateStageTemplateInput = {};
		if (values.name !== template.name) input.name = values.name;
		if (description && description !== (template.description ?? "")) {
			input.description = description;
		}
		if (Object.keys(input).length === 0) {
			toast.info("Nenhuma alteração para salvar.");
			onOpenChange(false);
			return;
		}
		try {
			await updateTemplate.mutateAsync(input);
			toast.success("Template atualizado com sucesso!");
			onOpenChange(false);
		} catch (err) {
			applyAPIError(err, "Houve um erro ao atualizar o template.");
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{mode === "create" ? "Novo template" : "Editar template"}
					</DialogTitle>
					<DialogDescription>
						Templates de etapas ficam disponíveis para todas as obras da conta.
					</DialogDescription>
				</DialogHeader>
				<form
					id="template-form"
					onSubmit={handleSubmit(onSubmit)}
					noValidate
					className="template-form"
				>
					<div className="form-group">
						<Label htmlFor="template-name">Nome</Label>
						<Input
							id="template-name"
							placeholder="Ex.: Obra residencial padrão"
							aria-invalid={!!errors.name}
							{...register("name")}
						/>
						{errors.name && <p className="form-error">{errors.name.message}</p>}
					</div>

					<div className="form-group">
						<Label htmlFor="template-description">Descrição</Label>
						<Textarea
							id="template-description"
							placeholder="Detalhes do template (opcional)"
							aria-invalid={!!errors.description}
							{...register("description")}
						/>
						{mode === "edit" && template?.description && (
							<p className="template-form__hint">
								A descrição não pode ser removida, apenas alterada.
							</p>
						)}
						{errors.description && (
							<p className="form-error">{errors.description.message}</p>
						)}
					</div>

					{formError && (
						<p role="alert" className="template-form__error">
							{formError}
						</p>
					)}
				</form>
				<DialogFooter>
					<Button
						type="button"
						variant="ghost"
						onClick={() => onOpenChange(false)}
						disabled={isPending}
					>
						Cancelar
					</Button>
					<Button type="submit" form="template-form" disabled={isPending}>
						{isPending && <Loader2 className="template-spinner" />}
						{mode === "create" ? "Criar template" : "Salvar alterações"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
